import * as actionTypes from './actionTypes';
import config from '../config';
import { APPOINTMENT_CRITERIA } from '../constants/appointmentCriteria';
import qs from 'qs';
import moment from 'moment';
import { Dispatch } from 'redux';

// Types for appointment action payloads
interface RequestConfig {
	method: string;
	url: string;
	headers?: Record<string, string>;
	data?: any;
}

interface ActionPayload {
	request: RequestConfig;
}

interface Action {
	type: string;
	payload?: ActionPayload;
}

// Appointment details interfaces
export interface AppointmentDetails {
	idCareOrderStatusReason?: number;
	careOrderVisitIdentifier?: string;
	correlationKey?: string;
	externalReferralOrderId?: string;
	idSlot?: number;
	patientReferenceId?: string;
	idPayorType?: number;
	idLanguage?: number;
	unmanagedReferringServiceName?: string;
	managedReferringServiceId?: number;
	referringOrganizationProviderId?: number;
	languageCode?: string;
	hasRequestedWaitlist?: boolean;
	homePhoneCallReminder?: boolean;
	homePhoneCallReminderNumber?: string;
	homePhoneRemindersHoursBefore?: number;
	mobilePhoneCallReminder?: boolean;
	mobilePhoneCallReminderNumber?: string;
	mobilePhoneCallRemindersHoursBefore?: number;
	mobilePhoneTextReminder?: boolean;
	mobilePhoneTextReminderNumber?: string;
	mobilePhoneTextRemindersHoursBefore?: number;
	emailReminder?: boolean;
	reminderEmail?: string;
	emailConfirmation?: boolean;
	confirmationEmail?: string;
	emailRemindersHoursBefore?: number;
	thirdPartyEmailConfirmation?: boolean;
	thirdPartyEmail?: string;
	reasonForAppointment?: string;
	idInsuranceState?: number;
	idInsurance?: number;
	clientIdentifier?: string;
	reservationId?: string;
	customFieldValues?: any;
	appointmentAttachments?: any;
	workboxId?: string;
	appointmentTypeId?: number;
	idServiceSite?: number;
	slotStartDateTime?: string;
	slotEndDateTime?: string;
	productInstanceConsumerId?: number;
	decisionSupportSessionId?: number;
	referenceId?: string;
	reasonForVisit?: string;
	providerResponse?: string;
	doUpdateReasonForVisit?: boolean;
	doUpdateProviderResponse?: boolean;
	appointmentRescheduleReferenceId?: string;
	rescheduleMethod?: string;
	unmanagedReferringSiteName?: string;
	managedReferringSiteId?: number;
	sourceChannel?: string;
	d2CInstanceName?: string;
	dotNetSessionId?: string;
	referralSiteId?: number;
	schedulingProductInstance?: number;
}

interface CancelRequest {
	reasonForCancellation?: string;
	sendCancellationEmailToPatient?: boolean;
	email?: string;
	languageCode?: string;
}

interface TimeSlotInfo {
	appointmentTypeId: number;
	serviceSiteId: number;
	timeSlotId: number;
	date: string;
	endTime: string;
	startTime: string;
	calendarId?: number; // Keep for backward compatibility
	[key: string]: any;
}

interface RequestDetails {
	patientId?: number;
	patientReferenceId?: string;
	referralSystemId?: number;
	memberId?: string;
	idUserReferrer?: string;
	idPgmPayorType?: number;
	idPgmSpecialty?: number;
	idLanguage?: number;
	reasonForReferral?: string;
	emailAttachments?: string;
	siteId?: number;
	referringServiceName?: string;
	idInsurance?: number;
	serviceName?: string;
	idServiceSite?: number;
	patientMessage?: string;
	uploadedFiles?: string[];
	preferredContactPhone?: string;
	preferredContactPhoneType?: string;
	schedulingInformation?: string;
	shouldSendConfirmationEmail?: boolean;
	alternateConfirmationEmail?: string;
	customValues?: any;
	patientNotificationLanguageCode?: string;
	patientConfirmationEmailEnabled?: boolean;
	patientConfirmationEmailDestinationEmail?: string;
	patientReminderSmsEnabled?: boolean;
	patientReminderSmsLeadTimeHours?: number;
	patientReminderSmsDestinationPhone?: string;
	patientReminderVoiceEnabled?: boolean;
	patientReminderVoiceLeadTimeHours?: number;
	patientReminderVoiceDestinationPhone?: string;
	patientReminderEmailEnabled?: boolean;
	patientReminderEmailLeadTimeHours?: number;
	patientReminderEmailDestinationEmail?: string;
}

interface UpdateAppointmentCriteria {
	referenceId: string;
	reasonForVisit: string;
	providerResponse: string;
	doUpdateReasonForVisit: boolean;
	doUpdateProviderResponse: boolean;
}

interface AgentInstructions {
	agentAvailabilityInstructions: string;
	agentReservationInstructions: string;
	agentPostBookingInstructions: string;
}

interface SymptomDetails {
	reasonForVisit: string;
}

interface SetAppointmentDetailsAction {
	type: string;
	agentInstructions: AgentInstructions;
	symptomDetails: SymptomDetails;
}

export function getAppointmentReferenceId(refType: string, refValue: string): Action {
	return {
		type: actionTypes.TOKEN_LINK_APPOINTMENT_REFID_LOOKUP,
		payload: {
			request: {
				method: 'get',
				url: `start/appointmentLookup?refType=${refType}&refValue=${refValue}`
			}
		}
	};
}

export function getAppointmentByReferenceId(referenceId: string): Action {
	return {
		type: actionTypes.APPOINTMENT_DETAILS,
		payload: {
			request: {
				method: 'get',
				url: `appointment/${referenceId}/details`
			}
		}
	};
}

export function getCancelledAppointmentByReferenceId(referenceId: string): Action {
	return {
		type: actionTypes.APPOINTMENT_DETAILS,
		payload: {
			request: {
				method: 'get',
				url: `cancelledAppointment/${referenceId}/details`
			}
		}
	};
}

export function getAppointmentRequest(requestId: string): Action {
	return {
		type: actionTypes.APPOINTMENT_REQUEST_DETAILS,
		payload: {
			request: {
				method: 'get',
				url: `appointment/request/${requestId}/details`
			}
		}
	};
}

export function getPreviousAppointmentByReferenceId(referenceId: string): Action {
	return {
		type: actionTypes.PREVIOUS_APPOINTMENT_DETAILS,
		payload: {
			request: {
				method: 'get',
				url: `appointment/${referenceId}/details`
			}
		}
	};
}

export function getReservationDetails(
	fce: string,
	patientReferenceId: string,
	serviceSiteId: number,
	insuranceProvider: number,
	payorType: number
): Action {
	return {
		type: actionTypes.APPOINTMENT_RESERVE_DETAILS,
		payload: {
			request: {
				method: 'post',
				url: `appointment/reservation/details`,
				data: {
					flexCalendarEntryId: fce,
					patientReferenceId: patientReferenceId,
					serviceSiteId: serviceSiteId,
					insuranceProviderId: insuranceProvider,
					payorTypeId: payorType,
				},
			}
		}
	};
}

export function getReservationDetailsOptionalPatient(
	flexCalendarEntryId: string | number,
	serviceSiteId: number,
	insuranceProviderId: number | null,
	payorTypeId: number | null,
	patientReferenceId: string | null = null,
): Action {
	return {
		type: actionTypes.APPOINTMENT_RESERVE_DETAILS,
		payload: {
			request: {
				method: 'post',
				url: `appointment/reservation/details/optionalpatient`,
				data: {
					flexCalendarEntryId,
					serviceSiteId,
					insuranceProviderId: insuranceProviderId,
					payorTypeId: payorTypeId,
					patientReferenceId,
				},
			},
		},
	};
}

export function bookAppointment(token: string, details: AppointmentDetails): Action {
	return {
		type: actionTypes.APPOINTMENT_BOOK,
		payload: {
			request: {
				method: 'post',
				url: 'appointment/book',
				data: {
					idCareOrderStatusReason: details.idCareOrderStatusReason,
					careOrderVisitIdentifier: details.careOrderVisitIdentifier,
					correlationKey: details.correlationKey,
					externalReferralOrderId: details.externalReferralOrderId,
					idSlot: details.idSlot,
					patientReferenceId: details.patientReferenceId,
					idPayorType: details.idPayorType,
					idLanguage: details.idLanguage,
					unmanagedReferringServiceName: details.unmanagedReferringServiceName,
					managedReferringServiceId: details.managedReferringServiceId,
					referringOrganizationProviderId: details.referringOrganizationProviderId,
					languageCode: details.idLanguage === config.notificationLanguages.spanish ? "ES" : "EN", //Legacy hardcodes these, D2c doesn't use them at all.. These codes aren't even in the DB
					hasRequestedWaitlist: details.hasRequestedWaitlist,
					homePhoneCallReminder: details.homePhoneCallReminder,
					homePhoneCallReminderNumber: details.homePhoneCallReminderNumber,
					homePhoneRemindersHoursBefore: details.homePhoneRemindersHoursBefore,
					mobilePhoneCallReminder: details.mobilePhoneCallReminder,
					mobilePhoneCallReminderNumber: details.mobilePhoneCallReminderNumber,
					mobilePhoneCallRemindersHoursBefore: details.mobilePhoneCallRemindersHoursBefore,
					mobilePhoneTextReminder: details.mobilePhoneTextReminder,
					mobilePhoneTextReminderNumber: details.mobilePhoneTextReminderNumber,
					mobilePhoneTextRemindersHoursBefore: details.mobilePhoneTextRemindersHoursBefore,
					emailReminder: details.emailReminder,
					reminderEmail: details.reminderEmail,
					emailConfirmation: details.emailConfirmation,
					confirmationEmail: details.confirmationEmail,
					emailRemindersHoursBefore: details.emailRemindersHoursBefore,
					reasonForAppointment: details.reasonForAppointment,
					idInsuranceState: details.idInsuranceState,
					idInsurance: details.idInsurance,
					clientIdentifier: details.clientIdentifier,
					reservationId: details.reservationId,
					customFieldValues: details.customFieldValues,
					appointmentAttachments: details.appointmentAttachments,
					workboxId: details.workboxId,
					appointmentTypeId: details.appointmentTypeId,
					idServiceSite: details.idServiceSite,
					slotStartDateTime: details.slotStartDateTime,
					slotEndDateTime: details.slotEndDateTime,
					productInstanceConsumerId: details.productInstanceConsumerId,
					decisionSupportSessionId: details.decisionSupportSessionId,
				},
				headers: {
					token: token,
				},
			}
		}
	};
}

export function updateAppointment(details: UpdateAppointmentCriteria): Action {
	return {
		type: actionTypes.APPOINTMENT_UPDATE,
		payload: {
			request: {
				method: 'put',
				url: 'appointment/update',
				data: {
					referenceId: details.referenceId,
					reasonForVisit: details.reasonForVisit,
					providerResponse: details.providerResponse,
					doUpdateReasonForVisit: details.doUpdateReasonForVisit,
					doUpdateProviderResponse: details.doUpdateProviderResponse,
				},
			}
		}
	};
}

export function rescheduleAppointment(
	token: string,
	details: AppointmentDetails,
	previousAppointmentReferenceId: string,
	rescheduleMethod: string
): Action {
	return {
		type: actionTypes.APPOINTMENT_RESCHEDULE,
		payload: {
			request: {
				method: 'post',
				url: 'appointment/reschedule',
				data: {
					careOrderVisitIdentifier: details.careOrderVisitIdentifier,
					correlationKey: details.correlationKey,
					externalReferralOrderId: details.externalReferralOrderId,
					idSlot: details.idSlot,
					patientReferenceId: details.patientReferenceId,
					idPayorType: details.idPayorType,
					idLanguage: details.idLanguage,
					unmanagedReferringServiceName: details.unmanagedReferringServiceName,
					managedReferringServiceId: details.managedReferringServiceId,
					referringOrganizationProviderId: details.referringOrganizationProviderId,
					languageCode: details.idLanguage === config.notificationLanguages.spanish ? "ES" : "EN",
					hasRequestedWaitlist: details.hasRequestedWaitlist,
					homePhoneCallReminder: details.homePhoneCallReminder,
					homePhoneCallReminderNumber: details.homePhoneCallReminderNumber,
					homePhoneRemindersHoursBefore: details.homePhoneRemindersHoursBefore,
					mobilePhoneCallReminder: details.mobilePhoneCallReminder,
					mobilePhoneCallReminderNumber: details.mobilePhoneCallReminderNumber,
					mobilePhoneCallRemindersHoursBefore: details.mobilePhoneCallRemindersHoursBefore,
					mobilePhoneTextReminder: details.mobilePhoneTextReminder,
					mobilePhoneTextReminderNumber: details.mobilePhoneTextReminderNumber,
					mobilePhoneTextRemindersHoursBefore: details.mobilePhoneTextRemindersHoursBefore,
					emailReminder: details.emailReminder,
					reminderEmail: details.reminderEmail,
					emailConfirmation: details.emailConfirmation,
					confirmationEmail: details.confirmationEmail,
					emailRemindersHoursBefore: details.emailRemindersHoursBefore,
					reasonForAppointment: details.reasonForAppointment,
					idInsuranceState: details.idInsuranceState,
					idInsurance: details.idInsurance,
					clientIdentifier: details.clientIdentifier,
					reservationId: details.reservationId,
					customFieldValues: details.customFieldValues,
					appointmentAttachments: details.appointmentAttachments,
					workboxId: details.workboxId,
					appointmentTypeId: details.appointmentTypeId,
					idServiceSite: details.idServiceSite,
					slotStartDateTime: details.slotStartDateTime,
					slotEndDateTime: details.slotEndDateTime,
					productInstanceConsumerId: details.productInstanceConsumerId,
					appointmentRescheduleReferenceId: previousAppointmentReferenceId,
					rescheduleMethod: rescheduleMethod,
					decisionSupportSessionId: details.decisionSupportSessionId,
				},
				headers: {
					token: token,
				},
			}
		}
	};
}

export function reserveAppointment(
	token: string,
	timeSlotInfo: TimeSlotInfo,
	reservationDuration: number,
	correlationKey: string,
	patientPool?: { uniqueToken?: string } | null
): Action {
	const data: Record<string, unknown> = {
		correlationKey: correlationKey,
		reservationDuration: reservationDuration,
		timeSlotRequestInfo: {
			...timeSlotInfo,
			serviceSiteId: timeSlotInfo.calendarId
		}, // TODO: refactor endpoint to use calendarId for consistency
	};
	if (patientPool != null && patientPool.uniqueToken != null && patientPool.uniqueToken !== '') {
		data.patientPool = { id: patientPool.uniqueToken, type: 'UniqueToken' };
	}
	return {
		type: actionTypes.APPOINTMENT_RESERVE,
		payload: {
			request: {
				method: 'post',
				url: 'appointment/reserve',
				headers: {
					token: token,
				},
				data,
			}
		}
	};
}

export function requestAppointment(token: string, details: RequestDetails): Action {
	return {
		type: actionTypes.APPOINTMENT_REQUEST,
		payload: {
			request: {
				method: 'post',
				url: 'appointment/request',
				data: {
					patientReferenceId: details.patientReferenceId,
					referralSystemId: details.referralSystemId,
					memberId: details.memberId,
					idUserReferrer: details.idUserReferrer,
					idPgmPayorType: details.idPgmPayorType,
					idPgmSpecialty: details.idPgmSpecialty,
					idLanguage: details.idLanguage,
					reasonForReferral: details.reasonForReferral,
					siteId: details.siteId,
					referringServiceName: details.referringServiceName,
					idInsurance: details.idInsurance,
					serviceName: details.serviceName,
					idServiceSite: details.idServiceSite,
					patientMessage: details.patientMessage,
					uploadedFiles: details.uploadedFiles,
					preferredContactPhone: details.preferredContactPhone,
					preferredContactPhoneType: details.preferredContactPhoneType,
					schedulingInformation: details.schedulingInformation,
					shouldSendConfirmationEmail: details.shouldSendConfirmationEmail,
					alternateConfirmationEmail: details.alternateConfirmationEmail,
					customValues: details.customValues,
					patientNotificationLanguageCode: details.patientNotificationLanguageCode,
					patientConfirmationEmailEnabled: details.patientConfirmationEmailEnabled,
					patientConfirmationEmailDestinationEmail: details.patientConfirmationEmailDestinationEmail,
					patientReminderSmsEnabled: details.patientReminderSmsEnabled,
					patientReminderSmsLeadTimeHours: details.patientReminderSmsLeadTimeHours,
					patientReminderSmsDestinationPhone: details.patientReminderSmsDestinationPhone,
					patientReminderVoiceEnabled: details.patientReminderVoiceEnabled,
					patientReminderVoiceLeadTimeHours: details.patientReminderVoiceLeadTimeHours,
					patientReminderVoiceDestinationPhone: details.patientReminderVoiceDestinationPhone,
					patientReminderEmailEnabled: details.patientReminderEmailEnabled,
					patientReminderEmailLeadTimeHours: details.patientReminderEmailLeadTimeHours,
					patientReminderEmailDestinationEmail: details.patientReminderEmailDestinationEmail,
				},
				headers: {
					token: token,
				},
			}
		}
	};
}

export function cancelAppointment(appointmentId: string, cancelRequest: CancelRequest, token: string): Action {
	return {
		type: actionTypes.APPOINTMENT_CANCEL,
		payload: {
			request: {
				method: 'post',
				url: `appointment/{appointmentId}/cancel`,
				headers: {
					token: token,
				},
				data: {
					reasonForCancellation: cancelRequest.reasonForCancellation,
					sendCancellationEmailToPatient: cancelRequest.sendCancellationEmailToPatient,
					email: cancelRequest.email,
					languageCode: cancelRequest.languageCode,
				},
			}
		}
	};
}

export function cancelReservation(token: string, reservationId: string): Action {
	return {
		type: actionTypes.APPOINTMENT_RESERVATION_CANCEL,
		payload: {
			request: {
				method: 'get',
				url: `appointment/reservation/${reservationId}/cancel/`,
				headers: {
					token: token,
				},
				data: {},
			}
		}
	};
}

export function getCustomFields(referralSystemId: string): Action {
	return {
		type: actionTypes.CUSTOM_FIELDS,
		payload: {
			request: {
				method: 'get',
				url: `appointment/customfields/${referralSystemId}/`,
			}
		}
	};
}

export function getReferringProviders(systemId: string): Action {
	return {
		type: actionTypes.GET_REFERRING_PROVIDERS,
		payload: {
			request: {
				method: 'get',
				url: `system/${systemId}/referringProviders/`,
			}
		}
	};
}

export function searchReferringProviders(referralSystemId: string, query: string): Action {
	return {
		type: actionTypes.GET_REFERRING_PROVIDERS,
		payload: {
			request: {
				method: 'get',
				url: `system/${referralSystemId}/referringProviders/${query}`,
			}
		}
	};
}

export function getOrganizationReferringProviders(): Action {
	return {
		type: actionTypes.GET_ORGANIZATION_REFERRING_PROVIDERS,
		payload: {
			request: {
				method: 'get',
				url: 'organizationReferringProviders',
			}
		}
	};
}

export function searchOrganizationProviders(query: string): Action {
	return {
		type: actionTypes.GET_ORGANIZATION_REFERRING_PROVIDERS,
		payload: {
			request: {
				method: 'get',
				url: `organizationReferringProviders/${query}`,
			}
		}
	};
}

export function setAppointmentDetailsFromDecisionSupportOutput(
	careOrderAppointmentDetails: Record<string, any>,
	decisionSupportAppointmentDetails: Record<string, any>
) {
	return function (dispatch: Dispatch) {
		dispatch(setAppointmentDetailsFromDecisionSupportOutputAction(careOrderAppointmentDetails, decisionSupportAppointmentDetails));
		return Promise.resolve();
	};
}

function setAppointmentDetailsFromDecisionSupportOutputAction(
	careOrderAppointmentDetails: Record<string, any>,
	decisionSupportAppointmentDetails: Record<string, any>
): SetAppointmentDetailsAction {
	const result: SetAppointmentDetailsAction = {
		type: actionTypes.SET_APPOINTMENT_DETAILS_FROM_DECISION_SUPPORT_OUTPUT,
		agentInstructions: {
			agentAvailabilityInstructions: decisionSupportAppointmentDetails[APPOINTMENT_CRITERIA.agentAvailabilityInstructions] || '',
			agentReservationInstructions: decisionSupportAppointmentDetails[APPOINTMENT_CRITERIA.agentReservationInstructions] || '',
			agentPostBookingInstructions: decisionSupportAppointmentDetails[APPOINTMENT_CRITERIA.agentPostBookingInstructions] || '',
		},
		symptomDetails: {
			reasonForVisit: decisionSupportAppointmentDetails[APPOINTMENT_CRITERIA.reasonForVisit] || careOrderAppointmentDetails[APPOINTMENT_CRITERIA.reasonForVisit] || '',
		},
	};

	return result;
}

export function clearSearchAppointmentContext(): Action {
	return {
		type: actionTypes.CLEAR_SEARCH_APPOINTMENT_CONTEXT
	};
}

export function getPracticeSitesForReferralSite(siteId: string): Action {
	return {
		type: actionTypes.GET_PRACTICE_SITES_FOR_REFERRAL_SITE,
		payload: {
			request: {
				method: 'get',
				url: `site/${siteId}/practices`,
			}
		}
	};
}

export function getServicesForReferralSiteAtPracticeSite(practiceSiteId: string): Action {
	return {
		type: actionTypes.GET_SERVICES_FOR_REFERRAL_SITE_AT_PRACTICE_SITE,
		payload: {
			request: {
				method: 'get',
				url: `site/${practiceSiteId}/services`,
			}
		}
	};
}

export function getAttendanceInfo(): Action {
	return {
		type: actionTypes.GET_ATTENDANCE_INFO,
		payload: {
			request: {
				method: 'get',
				url: 'attendanceinfo',
			}
		}
	};
}

export function updateAppointmentAttendance(updateAppointmentsCriteria: any): Action {
	return {
		type: actionTypes.UPDATE_APPOINTMENT_ATTENDANCE,
		payload: {
			request: {
				method: 'put',
				url: 'updateattendance',
				data: updateAppointmentsCriteria
			}
		}
	};
}